// TakeawayCard.tsx
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { BillType, ORDER_TYPE } from '../../../all-types';
import { deliveryOptions } from '../../../constants';
import { CommonBtn } from '../../../components';
import { updateTakeawayBillStatus } from '../../../services';
import { FC } from 'react';
import { BillPrinter } from '../../printer';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.6rem',
  height: 20,
  fontWeight: 700,
  textTransform: 'capitalize',
  borderRadius: 8,
}));

const OrderTypeBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  fontSize: '0.75rem',
  fontWeight: 500,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
}));

type TakeawayCardProps = {
  order: BillType;
  refersh?: () => void;
  viewOnly?: boolean;
};

const TakeawayCard: FC<TakeawayCardProps> = ({ order, refersh, viewOnly = false }) => {
  const theme = useTheme();

  const getTypeTextColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.freeTable?.main || theme.palette.success.main;
      case ORDER_TYPE.TAKEAWAY:
        return '#E7AA5A';
      default:
        return theme.palette.text.primary;
    }
  };

  const getTypeBorderColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return '#ADEED8';
      case ORDER_TYPE.TAKEAWAY:
        return '#FFE6BB';
      default:
        return theme.palette.divider;
    }
  };

  const getTypeColor = (type: ORDER_TYPE | string) => {
    switch (type) {
      case ORDER_TYPE.DINE_IN:
        return theme.palette.color8?.main || theme.palette.background.paper;
      case ORDER_TYPE.TAKEAWAY:
        return '#FFF9ED';
      default:
        return theme.palette.background.paper;
    }
  };

  const updateTakeawayOrderStatus = async (status = 'READY') => {
    try {
      if (order?._id) {
        await updateTakeawayBillStatus(order._id, status);
        refersh?.();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getInstructions = (inst?: string) => {
    if (!inst) return '';
    try {
      const instructions: { [key: string]: string } = JSON.parse(inst);
      return (
        Object.entries(instructions)
          ?.map(([key, value]) => `${key}: ${value}`)
          .join(', ') || ''
      );
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  if (!order) return null;

  const getTimeSinceOrder = () => {
    const orderTime = new Date(Number(order?.timeInfo?.timestamp || 0)).getTime();
    const now = Date.now();
    const diffInMinutes = Math.max(0, Math.floor((now - orderTime) / (1000 * 60)));
    return diffInMinutes;
  };

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card
        elevation={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          cursor: 'pointer',
          borderRadius: 3,
          backgroundColor: 'primary.light',
          '&:hover': { boxShadow: 6 },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Stack justifyContent="space-between" flex={1} gap={1}>
            {/* Header row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                <img
                  src={deliveryOptions[order?.takeAwayType || 'Take-Away/Parcel']?.logoUrl}
                  alt={order?.takeAwayType || 'Take-Away/Parcel'}
                  style={{ width: 35, height: 35, borderRadius: '10%' }}
                />
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {order?.takeAwayType}
                </Typography>
              </Stack>

              <Typography variant="body2" fontWeight="bold">
                {`Order No#${order?.sNo}`}
              </Typography>

              <Stack gap={0.5} alignItems="flex-end">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold">
                    Status:
                  </Typography>
                  <StatusChip
                    label={order.takeAwayStatus?.toLowerCase() || 'pending'}
                    sx={{
                      bgcolor: theme.palette.occupied?.main || theme.palette.warning.light,
                      color: 'warning.dark',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold">
                    Payment:
                  </Typography>
                  <StatusChip
                    label={order.modeOfPayment === 'CASH' ? 'Cash' : 'Paid'}
                    sx={{
                      bgcolor:
                        order.modeOfPayment === 'CASH'
                          ? theme.palette.occupied?.main || theme.palette.warning.light
                          : theme.palette.success.light,
                      color: order.modeOfPayment === 'CASH' ? 'warning.dark' : 'success.dark',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              </Stack>
            </Stack>

            <Divider />

            {/* Items */}
            <Box sx={{ mt: 1, maxHeight: 280, pr: 0.5, overflowY: 'auto' }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Items:
              </Typography>

              {(order.items || []).map((item: any, index: number) => {
                const rowKey = item?._id || item?.itemId || `${order._id || order.billNo}-${index}`;
                return (
                  <Stack key={rowKey} gap={1} mt={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="body2" component="div">
                        {index + 1}• {item.itemName} (x {item.noOfItems})
                      </Typography>
                      <Typography variant="caption" component="div">
                        {`${item.quantity <= 1 ? '' : item.quantity} ${item.quantityType || ''}`.trim()}
                      </Typography>
                    </Stack>
                    {!!item?.kitchInfo?.kitchenInstrcutions && (
                      <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {getInstructions(item?.kitchInfo?.kitchenInstrcutions)}
                      </Typography>
                    )}
                    <Divider />
                  </Stack>
                );
              })}
            </Box>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
              Amount:{' '}
              <Box component="span" fontWeight="bold">
                ₹{order?.billingInfo?.finalBillAmount || 0}
              </Box>
            </Typography>
          </Stack>
        </CardContent>

        {!viewOnly && (
          <Stack alignItems="flex-end" p={2} gap={2}>
            {order?.takeAwayStatus !== 'DONE' &&
              (order?.takeAwayStatus === 'READY' ? (
                <>
                  <CommonBtn variant="contained" color="success" onClick={() => updateTakeawayOrderStatus('DONE')}>
                    Mark as Complete
                  </CommonBtn>
                  <BillPrinter bill={order} takeawayKitchenPrint />
                </>
              ) : (
                <>
                  <CommonBtn variant="contained" onClick={() => updateTakeawayOrderStatus('READY')}>
                    Mark as Ready
                  </CommonBtn>
                  {/* <CommonBtn variant='contained' color='info' onClick={() => updateTakeawayOrderStatus("READY")}>Print</CommonBtn> */}
                </>
              ))}
          </Stack>
        )}

        <OrderTypeBadge
          sx={{
            bgcolor: getTypeColor(order.orderType),
            color: getTypeTextColor(order.orderType),
            borderTop: `1px dashed ${getTypeBorderColor(order.orderType)}`,
          }}
        >
          <Typography variant="caption" textTransform="capitalize" fontWeight={700}>
            {order.orderType.toLowerCase()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {getTimeSinceOrder()} mins
            </Typography>
          </Box>
        </OrderTypeBadge>
      </Card>
    </Grid>
  );
};

export { TakeawayCard };
